class CourseTimetable extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.endpoint = this.dataset.endpoint || '/apps/course-timetable';
    this.startHour = Number(this.dataset.startHour || 8);
    this.endHour = Number(this.dataset.endHour || 20);
    this.hourHeight = Number(this.dataset.hourHeight || 64);
    this.selectedDay = 0;
    this.weekStart = this.getMonday(new Date());
    this.previousButton = this.querySelector('[data-previous-week]');
    this.nextButton = this.querySelector('[data-next-week]');
    this.todayButton = this.querySelector('[data-current-week]');
    this.dateRange = this.querySelector('[data-date-range]');
    this.dayHeaders = this.querySelector('[data-day-headers]');
    this.dayTabs = this.querySelector('[data-day-tabs]');
    this.grid = this.querySelector('[data-calendar-grid]');
    this.calendarShell = this.querySelector('[data-calendar-shell]');
    this.calendarStage = this.querySelector('[data-calendar-stage]');
    this.status = this.querySelector('[data-timetable-status]');
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartedAt = 0;
    this.resizeTimer = null;
    this.onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.sizeMobileOverview(), 80);
    };
    window.addEventListener('resize', this.onResize, { passive: true });
    this.previousButton?.addEventListener('click', () => this.changeWeek(-7));
    this.nextButton?.addEventListener('click', () => this.changeWeek(7));
    this.todayButton?.addEventListener('click', () => {
      this.weekStart = this.getMonday(new Date());
      this.selectedDay = 0;
      this.load();
    });
    this.calendarShell?.addEventListener('touchstart', (event) => {
      if (!window.matchMedia('(max-width: 749px)').matches || event.touches.length !== 1) return;
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.touchStartedAt = Date.now();
    }, { passive: true });
    this.calendarShell?.addEventListener('touchend', (event) => {
      if (!window.matchMedia('(max-width: 749px)').matches || !event.changedTouches.length) return;
      const deltaX = event.changedTouches[0].clientX - this.touchStartX;
      const deltaY = event.changedTouches[0].clientY - this.touchStartY;
      const duration = Date.now() - this.touchStartedAt;
      this.touchStartX = 0;
      this.touchStartY = 0;
      if (duration > 700 || Math.abs(deltaX) < 65 || Math.abs(deltaX) < Math.abs(deltaY) * 1.35) return;
      if (deltaX < 0 && !this.nextButton?.disabled) this.changeWeek(7);
      if (deltaX > 0 && !this.previousButton?.disabled) this.changeWeek(-7);
    }, { passive: true });
    this.load();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  sizeMobileOverview() {
    if (!this.calendarShell || !this.calendarStage) return;
    if (!window.matchMedia('(max-width: 749px)').matches) {
      this.calendarStage.style.removeProperty('--mobile-calendar-scale');
      this.calendarShell.style.removeProperty('height');
      return;
    }
    const naturalWidth = 906;
    const scale = Math.min(1, this.calendarShell.clientWidth / naturalWidth);
    this.calendarStage.style.setProperty('--mobile-calendar-scale', String(scale));
    const naturalHeight = this.calendarStage.scrollHeight;
    this.calendarShell.style.height = `${Math.ceil(naturalHeight * scale)}px`;
  }

  getMonday(input) {
    const date = new Date(input);
    date.setHours(12, 0, 0, 0);
    const day = date.getDay();
    date.setDate(date.getDate() + (day === 0 ? -6 : 1 - day));
    return date;
  }

  addDays(input, amount) {
    const date = new Date(input);
    date.setDate(date.getDate() + amount);
    return date;
  }

  isoDate(input) {
    const year = input.getFullYear();
    const month = String(input.getMonth() + 1).padStart(2, '0');
    const day = String(input.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatRange(start) {
    const end = this.addDays(start, 5);
    const sameMonth = start.getMonth() === end.getMonth();
    const startText = start.toLocaleDateString('en-ZA', { day: 'numeric', month: sameMonth ? undefined : 'short' });
    const endText = end.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long' });
    return `${startText}–${endText}`;
  }

  changeWeek(amount) {
    const currentWeek = this.getMonday(new Date());
    const requestedWeek = this.addDays(this.weekStart, amount);
    this.weekStart = requestedWeek < currentWeek ? currentWeek : requestedWeek;
    this.selectedDay = 0;
    this.load();
  }

  minutes(value) {
    const [hours, minutes] = value.split(':').map(Number);
    return hours * 60 + minutes;
  }

  escape(value) {
    const element = document.createElement('div');
    element.textContent = String(value ?? '');
    return element.innerHTML;
  }

  colorIndex(title) {
    const name = String(title || '').trim().toLowerCase();
    if (name.includes('learn to sew')) return 0;
    if (name.includes('intermediate')) return 1;
    if (name.includes('pattern')) return 2;
    if (name.includes('kitchen')) return 3;
    if (name.includes('one on one')) return 4;
    if (name.includes('confident beginner')) return 5;
    if (name.includes('open sew')) return 6;
    return Array.from(name).reduce((total, letter) => total + letter.charCodeAt(0), 0) % 8;
  }

  openSchedule(slot, button) {
    const existing = document.querySelector('[data-course-schedule-overlay]');
    if (existing) {
      const sameSchedule = existing.dataset.scheduleId === String(slot.id);
      existing.closest('.course-timetable__slot')?.classList.remove('is-schedule-open');
      existing.remove();
      if (sameSchedule) return;
    }
    const dates = Array.isArray(slot.courseDates) ? slot.courseDates : [];
    const rows = dates.map((item) => {
      const date = new Date(`${item.date}T12:00:00`);
      const label = date.toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' });
      return `<li><span>Week ${item.weekNumber}</span><strong>${this.escape(label)}</strong></li>`;
    }).join('');
    const overlay = document.createElement('div');
    overlay.className = 'course-timetable__schedule-inline';
    overlay.dataset.courseScheduleOverlay = '';
    overlay.dataset.scheduleId = String(slot.id);
    overlay.innerHTML = `<div class="course-timetable__schedule-modal" role="region" aria-label="Course dates"><span class="course-timetable__schedule-kicker">Complete course schedule</span><ul>${rows}</ul>${slot.soldOut ? '<span class="course-timetable__schedule-sold-out">Sold out</span>' : `<a class="course-timetable__schedule-book" href="${this.escape(slot.productUrl)}&from_timetable=1">Choose this course →</a>`}</div>`;
    const card = button.closest('.course-timetable__slot');
    if (!card) return;
    card.classList.add('is-schedule-open');
    card.appendChild(overlay);
    const close = () => {
      card.classList.remove('is-schedule-open');
      overlay.remove();
    };
    const closeOutside = (event) => {
      if (!overlay.contains(event.target) && event.target !== button) {
        close();
        document.removeEventListener('click', closeOutside);
      }
    };
    setTimeout(() => document.addEventListener('click', closeOutside), 0);
  }

  renderDays() {
    const days = Array.from({ length: 6 }, (_, index) => this.addDays(this.weekStart, index));
    const currentWeek = this.getMonday(new Date());
    const isCurrentWeek = this.weekStart <= currentWeek;
    this.previousButton.disabled = isCurrentWeek;
    this.previousButton.setAttribute('aria-disabled', String(isCurrentWeek));
    const hasFutureClasses = this.hasFutureClasses !== false;
    this.nextButton.disabled = !hasFutureClasses;
    this.nextButton.setAttribute('aria-disabled', String(!hasFutureClasses));
    this.dateRange.textContent = this.formatRange(this.weekStart);
    this.dayHeaders.innerHTML = days.map((date) => `<div class="course-timetable__day-heading"><strong>${date.toLocaleDateString('en-ZA', { weekday: 'long' })}</strong><span>${date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}</span></div>`).join('');
    const courseDates = new Set((this.slots || []).map((slot) => slot.date));
    this.dayTabs.innerHTML = days.map((date, index) => {
      const hasCourse = courseDates.has(this.isoDate(date));
      return `<button type="button" class="course-timetable__day-tab${hasCourse ? ' has-course' : ''}${index === this.selectedDay ? ' is-active' : ''}" data-day-index="${index}"><span>${date.toLocaleDateString('en-ZA', { weekday: 'short' })}</span><strong>${date.getDate()}</strong></button>`;
    }).join('');
    this.dayTabs.querySelectorAll('[data-day-index]').forEach((button) => {
      button.addEventListener('click', () => {
        this.selectedDay = Number(button.dataset.dayIndex);
        this.render(this.slots || []);
      });
    });
  }

  render(slots) {
    try {
      this.slots = Array.isArray(slots) ? slots : [];
      const visibleSlots = this.slots;
      this.renderDays();
      const totalHours = this.endHour - this.startHour;
      const mobileOverview = window.matchMedia('(max-width: 749px)').matches;
      const rowHeight = Math.round(this.hourHeight * (mobileOverview ? 1.45 : 1.32));
      const height = totalHours * rowHeight;
      const slotsByDate = {};
      visibleSlots.forEach((slot) => {
        if (!slotsByDate[slot.date]) slotsByDate[slot.date] = [];
        slotsByDate[slot.date].push(slot);
      });

      let hourLines = '';
      for (let index = 0; index <= totalHours; index += 1) {
        const hour = this.startHour + index;
        const top = index * rowHeight;
        hourLines += `<div class="course-timetable__hour-line" style="top:${top}px"></div>`;
        hourLines += `<div class="course-timetable__time" style="top:${top}px">${String(hour).padStart(2, '0')}:00</div>`;
      }

      let lanes = '';
      for (let dayIndex = 0; dayIndex < 6; dayIndex += 1) {
        const date = this.isoDate(this.addDays(this.weekStart, dayIndex));
        const daySlots = slotsByDate[date] || [];
        let cards = '';
        daySlots.forEach((slot) => {
          const start = this.minutes(slot.startTime);
          const end = this.minutes(slot.endTime);
          const top = Math.max(3, ((start - this.startHour * 60) / 60) * rowHeight + 3);
          const cardHeight = Math.max(58, ((end - start) / 60) * rowHeight - 6);
          const isFollowUp = Number(slot.weekNumber || 1) > 1;
          const courseColor = `color-${this.colorIndex(slot.title) + 1}`;
          const color = isFollowUp ? `${courseColor} follow-up` : (slot.soldOut ? 'sold-out' : courseColor);
          const availability = slot.soldOut ? 'Sold out' : `${slot.available} space${slot.available === 1 ? '' : 's'}`;
          const scheduleButton = !isFollowUp && Number(slot.weeks || 1) > 1 ? `<button type="button" class="course-timetable__dates" data-schedule-id="${this.escape(slot.id)}">${slot.weeks} weeks · View all dates</button>` : '';
          const choose = !isFollowUp && !slot.soldOut ? `<a class="course-timetable__choose" href="${this.escape(slot.productUrl)}&from_timetable=1">Choose <span aria-hidden="true">→</span></a>` : '';
          const weekLabel = isFollowUp ? `<span class="course-timetable__follow-up-label">Week ${slot.weekNumber} · Follow-up class</span>` : '';
          const seatLabel = isFollowUp
            ? ''
            : (slot.soldOut
              ? `<span class="course-timetable__availability is-sold-out">${availability}</span>`
              : `<a class="course-timetable__availability is-bookable" href="${this.escape(slot.productUrl)}&from_timetable=1" aria-label="Choose ${this.escape(slot.title)}; ${availability}">${availability}</a>`);
          const content = `<strong class="course-timetable__slot-title">${this.escape(slot.title)}</strong><span class="course-timetable__slot-time">${this.escape(slot.startTime)}–${this.escape(slot.endTime)}</span>${weekLabel}${seatLabel}${scheduleButton}${choose}`;
          cards += `<div class="course-timetable__slot ${color}${isFollowUp ? ' is-follow-up' : ''}" style="top:${top}px;height:${cardHeight}px"${isFollowUp ? ' aria-label="Follow-up class; booking unavailable"' : ''}>${content}</div>`;
        });
        const selected = dayIndex === this.selectedDay ? ' is-selected' : '';
        const empty = daySlots.length === 0 ? '<span class="course-timetable__empty-day">No classes</span>' : '';
        lanes += `<div class="course-timetable__lane${selected}">${cards}${empty}</div>`;
      }

      this.grid.style.height = `${height}px`;
      this.grid.innerHTML = `<div class="course-timetable__time-layer">${hourLines}</div><div class="course-timetable__lanes">${lanes}</div>`;
      this.grid.querySelectorAll('[data-schedule-id]').forEach((button) => {
        button.addEventListener('click', () => {
          const slot = this.slots.find((item) => String(item.id) === button.dataset.scheduleId);
          if (slot) this.openSchedule(slot, button);
        });
      });
      this.status.textContent = visibleSlots.length ? `${visibleSlots.length} course${visibleSlots.length === 1 ? '' : 's'} available to book` : `No new courses start during ${this.formatRange(this.weekStart)}.`;
      this.status.classList.toggle('is-empty', visibleSlots.length === 0);
      requestAnimationFrame(() => this.sizeMobileOverview());
    } catch (error) {
      console.error('Course timetable render failed:', error);
      this.grid.innerHTML = '';
      this.status.textContent = 'The timetable data loaded, but the calendar could not be displayed. Please refresh the page.';
      this.status.classList.add('is-error');
    }
  }

  async load() {
    this.classList.add('is-loading');
    this.status.classList.remove('is-error');
    this.status.textContent = 'Loading the latest classes and availability…';
    this.renderDays();
    try {
      const response = await fetch(`${this.endpoint}?week=${this.isoDate(this.weekStart)}`, { headers: { Accept: 'application/json' } });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || 'The timetable could not be loaded.');
      this.hasFutureClasses = data.hasFutureClasses !== false;
      this.render(data.slots || []);
    } catch (error) {
      this.grid.innerHTML = '';
      this.status.textContent = error.message || 'The timetable could not be loaded. Please refresh the page.';
      this.status.classList.add('is-error');
    } finally {
      this.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('course-timetable')) customElements.define('course-timetable', CourseTimetable);
